import { useEffect, useRef, useState } from "react";
import { SendIcon } from "../Svg";
import { Button } from "../Button";
import _ from "lodash";
import cx from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { SelectInput } from "../form/SelectInput";
import UIState, { selectMobileLayout } from "../../store/UIState";
import { createSelector } from "reselect";
import { ModalTypes, PredictionStatus } from "../../constants";
import { AiOndemandApi, PREDICTION_STARTED, PREDICTION_STOPPED, selectApiKeys, selectPredictionId, selectPredictionStatus } from "../../store/models/AiOndemandApi";
import { getOndemandErrorMsg, extractErrorText, readFileAsDataURL } from "../../utils";
import { selectCurrentUser } from "../../store/models/User";
import { LanguageChoices } from "../../constants/ondemandApi";
import Loader from "../Loader";
import { AudioInput } from "../form/AudioInput";


const AudioMessage = ({ message }) => {
  const error = message.error;
  const audioUrl = message.audioData?.path;
  return (
    <div className={cx("ResultBox")}>
      {error ? <div className="ResultBox__Error">
        {error}
      </div> : <div className={"ResultBox__Text"}>
        <div className={"ResultBox__Text--label"}>
          Voice cloning complete. Here is the result:
        </div>
        <audio controls>
          <source src={audioUrl} type="audio/mpeg" />
          Your browser does not support the audio element.
        </audio>
      </div>}
    </div>)
}

const AudioLoadingItem = ({ elapsedTime, predictionStatus }) => {
  const isLoadingPrediction = predictionStatus === PredictionStatus.IN_PROGRESS
    || predictionStatus === PredictionStatus.STARTED;

  if (isLoadingPrediction) {
    return (<div className={cx("ResultBox")}>
      <div className={"ResultBox__Loading"}>
        <div className={"ResultBox__Loading__TopWrapper"}>
          Processing | {elapsedTime}s
        </div>
        <div className={"ResultBox__Loading__CenterWrapper"}>
          <Loader color={"green"} />
        </div>
        <div className={"ResultBox__Loading__BottomWrapper"}>Audio generation in progress...</div>
      </div>
    </div>)
  }
}

const ChatMessage = ({ message, showcaseModelUrl, mName }) => {

  const audioUrl = !mName
    ? `${showcaseModelUrl}/file=${message.audio.path}`
    : `${message.audio.path}`;

  return (
    <div className={cx("UserMessageContainer")}>
      <div className="UserMessage">
        <div className={"UserMessage__PromptLabel"}>Source audio</div>
        <div className={"UserMessage__PromptValue"}>
          <audio controls>
            <source src={audioUrl} type="audio/mpeg" />
            Your browser does not support the audio element.
          </audio>
        </div>
        <div className={"UserMessage__PromptLabel"}>Text</div>
        <div className={"UserMessage__PromptValue"}>
          {message.text}
        </div>
        <div className={"UserMessage__PromptLabel"}>Language</div>
        <div className={"UserMessage__PromptValue"}>
          {LanguageChoices[message.language]}
        </div>
      </div>

    </div>)
}

const selector = createSelector(
  selectMobileLayout,
  selectApiKeys,
  selectPredictionStatus,
  selectPredictionId,
  selectCurrentUser,
  (isMobile, apiKeys, predictionStatus, predictionId, curUser) => ({
    isMobile,
    apiKeys,
    predictionStatus,
    predictionId,
    curUser
  }
  ))

let intervalId = null;

export const ShowcaseAudioToAudio = ({ showcaseModel, className, mName = null }) => {
  const dispatch = useDispatch();
  const {
    isMobile,
    apiKeys,
    predictionStatus,
    predictionId,
    curUser
  } = useSelector(state => selector(state));
  const apiKey = _.get(apiKeys, '0.key');
  const [textareaValue, setTextareaValue] = useState('');
  const [messages, setMessages] = useState([]);
  const chatboxRef = useRef(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [currentAudio, setCurrentAudio] = useState(null);
  const languageOptions = Object.keys(LanguageChoices).map(k => ({ label: LanguageChoices[k], value: k }));
  const [language, setLanguage] = useState({ label: LanguageChoices.en, value: 'en' });
  const showcaseModelUrl = showcaseModel?.urls;
  const audioUrl = currentAudio
    ? (!mName
      ? `${showcaseModelUrl}/file=${currentAudio.path}`
      : `${currentAudio.path}`)
    : null;
  const [currentAudioFile, setCurrentAudioFile] = useState();
  const [audioUploading, setAudioUploading] = useState(false);


  useEffect(() => {
    return async () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, []);

  useEffect(() => {
    if (!mName) return;
    if (messages.length > 2) {
      onScrollToPageBottom();
    }
  }, [messages])

  const handleSubmit = async (event) => {
    if (predictionStatus === PredictionStatus.IN_PROGRESS) {
      onStopGenerating(event);
    } else {
      sendMessage(event);
    }
  }

  const onStopGenerating = (event) => {
    event.preventDefault();

    dispatch(UIState.actions.showModal(ModalTypes.CONFIRM, {
      title: "Stop generating",
      message: "Are you sure you want to stop the ongoing generation?",
      onConfirm: onConfirmStopGenerating,
      confirmLabel: "Yes",
      cancelLabel: "No",
      size: 's'
    }))
  }

  const onConfirmStopGenerating = async () => {
    stopInterval();
    try {
      setMessages((messages) => messages.slice(0, -2))
      if (mName) {
        await dispatch(AiOndemandApi.actions.stopHostedPredict(predictionId, apiKey));
        toast.success("Stopped the generation process.")
      }
    } catch (e) {
      toast.success(`Failed to stop the generation. Error: ${e.message}`)
    }
  }


  const startStopwatch = () => {
    if (intervalId) {
      stopInterval()
    }
    const startTime = new Date().getTime()
    intervalId = setInterval(() => {
      const currentTime = new Date().getTime()
      setElapsedTime(((currentTime - startTime) / 1000).toFixed(0))
    }, 1000)

  }

  const stopInterval = () => {
    clearInterval(intervalId)
    intervalId = null
    setElapsedTime(0)
  }

  const sendMessage = async (event) => {
    event.preventDefault();

    if (mName) setTextareaValue('');

    const userMsg = { text: textareaValue, language: language.value, audio: currentAudio, type: 'prompt' };
    const botMsg = { type: 'loading' }
    const newMessages = [...messages, userMsg, botMsg];

    setMessages(newMessages)
    const message = { text: textareaValue, language: language.value, audio: currentAudio };

    generateAudio(message, newMessages.length - 1)

    setTimeout(() => {
      if (!_.isNil(chatboxRef) && !_.isNil(chatboxRef.current)) {
        chatboxRef.current.scrollTop = chatboxRef.current.scrollHeight;
      }
    }, 1000)
  };

  const generateAudio = async (message, index) => {
    dispatch({ type: PREDICTION_STARTED });
    startStopwatch()
    let audioPresignedUrl = {};

    try {
      await fetchPreSignUrl();
      await dispatch(AiOndemandApi.actions.uploadImageToBucket(currentAudioFile, _.get(audioPresignedUrl, `presigned_url`)));

      onRemoveAudio();
      const params = {
        "voice": audioPresignedUrl.filename,
        "text": message.text,
        "language": message.language
      };

      let audio;
      if (mName) {
        startStopwatch();
        let result = await dispatch(AiOndemandApi.actions.startHostedPredict(params, mName, apiKey));
        audio = _.get(result, 'body.infer_requests.0.output.audio_url')
        stopInterval();
      }
      if (!audio) return;
      setMessages(oldMessages => {
        const newMessages = [...oldMessages];
        newMessages[index].audioData = { prompt: message.text, path: audio };
        newMessages[index].type = 'result';
        return newMessages
      });
    } catch (e) {
      const errMsg = getOndemandErrorMsg(e.message, curUser);
      const toastErr = extractErrorText(errMsg);
      toast.error(toastErr)
      dispatch({ type: PREDICTION_STOPPED })
      stopInterval()
      setMessages(oldMessages => {
        const newMessages = [...oldMessages];
        newMessages[index].type = 'result';
        newMessages[index].error = errMsg;
        return newMessages
      });
    }

    async function fetchPreSignUrl() {
      if (!mName || !apiKey) return;
      let res = await dispatch(AiOndemandApi.actions.presignUrl('whisper', null, apiKey));
      audioPresignedUrl = _.get(res, "audio_filename");
    }
  }

  const onTextareaChange = (event) => {
    setTextareaValue(event.target.value)
  }

  const onAudioSelected = async (file) => {
    if (!file) return;

    try {
      setAudioUploading(true)
      let audioPath = '';
      if (!mName) {
        //showcase template
      } else {
        audioPath = await readFileAsDataURL(file);
        setCurrentAudioFile(file)
      }
      setAudioUploading(false)
      const audioData = {
        "meta": {
          "_type": "gradio.FileData"
        },
        "mime_type": file.type,
        "orig_name": file.name,
        "path": audioPath,
        "size": file.size,
      }

      setCurrentAudio(audioData)
    } catch (e) {
      setAudioUploading(false);
      console.log('e:', e.message)
      toast.error("Failed to upload audio");
    }
  }

  const onRemoveAudio = () => {
    setCurrentAudio(null)
  }

  const onScrollToPageBottom = () => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  return (
    <div className={cx("ShowcaseAudioToAudio", className)}>
      <div className="ShowcaseAudioToAudio__container">

        <div className={"ShowcaseAudioToAudio__SidePanels"}>
          <div className={"SidePanel"}>
            {!isMobile && <div className={"ShowcaseAudioToAudio__LeftInfo"}>
              <div className={"SidePanel__Title"}>Model</div>
              <SelectInput className={"SidePanel__Select"}
                value={{ label: 'Voice Cloning', value: 'Voice Cloning' }}
                isDisabled={!!mName}
              />
            </div>}

            <form className="SidePanel__form" onSubmit={handleSubmit}>
              <AudioInput
                name={"audio"}
                value={audioUrl}
                onChange={onAudioSelected}
                onRemove={onRemoveAudio}
                formats={'audio/*'}
                displayName={currentAudioFile?.name}
              />
              <div className={"SidePanel__Header"}>
                <div className={"SidePanel__Header__Prompt"}>Text</div>
              </div>
              <textarea name="message"
                className="SidePanel__input"
                value={textareaValue}
                onChange={onTextareaChange}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    sendMessage(event);
                  }
                }}
                placeholder="Eg. This is a demo of the Voice Cloning model."
                disabled={predictionStatus !== PredictionStatus.STOPPED}
              />
              <div className={"SidePanel__Header"}>
                <div className={"SidePanel__Header__Prompt"}>Language</div>
              </div>
              <SelectInput className={"SidePanel__Select"}
                options={languageOptions}
                value={language}
                onChange={setLanguage}
                styles={customStyles}
              />

              <div className={"SidePanel__ActionButtons"}>
                <Button
                  title={"Clear"}
                  color={"grey"}
                  className="SidePanel__ActionButtons__Button"
                  disabled={_.isNil(currentAudio)}
                  onClick={onRemoveAudio}
                />

                {(predictionStatus === PredictionStatus.IN_PROGRESS)
                  ? <Button
                    title={"Stop generating"}
                    onClick={onStopGenerating}
                    className="SidePanel__ActionButtons__Button"
                    color={"red"}
                  />
                  : <Button
                    title={"Send"}
                    onClick={sendMessage}
                    icon={audioUploading ? null : <SendIcon />}
                    className="SidePanel__ActionButtons__Button"
                    color={"green"}
                    loading={audioUploading}
                    disabled={_.isNil(currentAudio)
                      || predictionStatus === PredictionStatus.STARTED} />}
              </div>
            </form>
          </div>
        </div>

        <div className={cx("ShowcaseAudioToAudio__RightSide")}>
          {!_.isEmpty(messages) && <div className={"MessagesWrapper"} ref={chatboxRef}>
            {messages.map((item, index) => {
              switch (item.type) {
                case 'result':
                  return (<AudioMessage key={index} message={item} />)
                case 'loading':
                  return (<AudioLoadingItem key={index} elapsedTime={elapsedTime} predictionStatus={predictionStatus} />)
                case 'prompt':
                  return (<ChatMessage key={index} message={item} showcaseModelUrl={showcaseModelUrl} mName={mName} />)
              }
            })}
          </div>
          }
        </div>
      </div>

    </div>
  );

}

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#131722',
    borderColor: state.isFocused ? '#18C99D' : '#3D4463',
    borderRadius: '6px',
    color: '#8A8FB5',
    outline: 'none',
    boxShadow: 'none',
    ':hover': {
      borderColor: '#18C99D', // Add your hover styles here
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? 'rgba(24, 201, 157, 0.1)' : '#131722',
    color: state.isFocused ? '#18C99D' : state.isSelected ? '#18C99D' : '#8A8FB5',
    fontFamily: 'Gilroy',
    paddingLeft: '10px',
    fontWeight: '500',
    fontSize: '14px',
    paddingTop: '11.5px',
    paddingBottom: '11.5px',
    position: 'relative',
  }),
  singleValue: (provided, state) => ({
    ...provided,
    marginLeft: '7px',
    borderColor: state.isFocused ? '#18C99D' : '#3D4463',
    color: '#8A8FB5',
    fontFamily: 'Gilroy',
    fontWeight: '500',
    fontSize: '14px',
    height: '15px',
    outline: 'none',
  }),
  indicatorsContainer: (provided, { isDisabled }) => ({
    ...provided,
    display: isDisabled ? 'none' : 'flex',
  }),
  indicatorSeparator: (provided, state) => ({
    ...provided,
    display: 'none'
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: '#8A8FB5',
    marginRight: '8px',
    ':hover': {
      color: '#8A8FB5', // Add your hover styles here
    },
  }),
  menu: (provided, state) => ({
    ...provided,
    backgroundColor: '#131722',
    border: '1px solid #3D4463',
    borderRadius: '6px',
    scrollbarColor: '#3D4463',
    maxHeight: '300px',
  }),
  menuList: (provided, state) => ({
    ...provided,
    maxHeight: '300px',
  }),
}